import { useCallback, useEffect, useState } from 'react'

import { AxiosError } from 'axios'

import { subdelegatedClient } from 'adapters/http'
import { SubdelegatedErrorResponse, SubdelegatedRole } from 'models/interface/subdelegated'

export type GuardStatus =
  | 'loading'
  | 'hasLink'
  | 'noLink'
  | 'expired'
  | 'alreadyUsed'
  | 'notFound'
  | 'invalidRole'
  | 'error'

type UseLinkStatusResult = {
  status: GuardStatus
  role?: SubdelegatedRole | null
  email?: string
  lpDocument?: string
  errorMessage: string
  retry: () => void
}

const DEFAULT_ERROR_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente mais tarde ou entre em contato com o seu consultor.'

const isKnownRole = (role: unknown): role is SubdelegatedRole =>
  (Object.values(SubdelegatedRole) as unknown[]).includes(role)

const isExpiredInviteMessage = (details: string | undefined): boolean =>
  details?.toLowerCase().includes('expirado') ?? false

export const useLinkStatus = (token: string | undefined, isReady: boolean): UseLinkStatusResult => {
  const [status, setStatus] = useState<GuardStatus>('loading')
  const [role, setRole] = useState<SubdelegatedRole | null | undefined>()
  const [email, setEmail] = useState<string | undefined>()
  const [lpDocument, setLpDocument] = useState<string | undefined>()
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE)
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    setStatus('loading')
    setAttempt(current => current + 1)
  }, [])

  useEffect(() => {
    if (!isReady) return undefined

    let isMounted = true

    subdelegatedClient
      .checkInvite(token ?? '')
      .then(({ data }) => {
        if (!isMounted) return

        if (data.role != null && !isKnownRole(data.role)) {
          setStatus('invalidRole')
          return
        }

        setRole(data.role)
        setEmail(data.email)
        setLpDocument(data.lpDocument)
        setStatus(data.isCashMember ? 'hasLink' : 'noLink')
      })
      .catch((error: AxiosError<SubdelegatedErrorResponse>) => {
        if (!isMounted) return

        if (error.response?.status === 404) {
          setStatus('notFound')
          return
        }

        if (error.response?.status === 409) {
          setStatus('alreadyUsed')
          return
        }

        if (error.response?.status === 400 && isExpiredInviteMessage(error.response.data?.details)) {
          setStatus('expired')
          return
        }

        setErrorMessage(error.response?.data?.details || DEFAULT_ERROR_MESSAGE)
        setStatus('error')
      })

    return () => {
      isMounted = false
    }
  }, [isReady, token, attempt])

  return { status, role, email, lpDocument, errorMessage, retry }
}
